'use client';
import dynamic from 'next/dynamic';
import { VALIDITY } from '@/constants';
import { useValidator } from '@/hooks';
import { Config, Fields } from '@/hooks/useValidator/interfaces';
import { useEffect, useRef, useState } from 'react';
import { Headline } from '@/components/molecules/Headline';
import { validateForm, validateInput } from '@/packages/form';
import useErrorCount from '@/hooks/useErrorCount';
const Grid = dynamic(() => import('de-sxm-reactlib-2/components/atoms/Layout/Grid/Grid'));
const StyledContainer = dynamic(
    () => import('de-sxm-reactlib-2/components/atoms/Layout/StyledContainer/StyledContainer'),
);
const Text = dynamic(() => import('de-sxm-reactlib-2/components/atoms/Text/Text'));
const Button = dynamic(() => import('de-sxm-reactlib-2/components/atoms/Button/Button'));
const Fieldset = dynamic(() => import('de-sxm-reactlib-2/components/atoms/Inputs/Fieldset/Fieldset'));
import Input from 'de-sxm-reactlib-2/components/atoms/Inputs/Input/Input';
import { PiiEnv } from '@/../properties/interface';
const Textarea = dynamic(() => import('de-sxm-reactlib-2/components/atoms/Inputs/Input/Textarea'));
const Form = dynamic(() => import('de-sxm-reactlib-2/components/atoms/Inputs/Form/Form'));
const FormErrorDisplay = dynamic(
    () => import('de-sxm-reactlib-2/components/atoms/Inputs/FormErrorDisplay/FormErrorDisplay'),
);

type SubmitState = 'untouched' | 'failed' | 'success' | 'error';

interface ServerFields {
    error: string;
    message: string;
    name: string;
}

interface Props {
    piiEnv: PiiEnv;
}

export default function PartnerContactUsFormClient({ piiEnv }: Props): React.JSX.Element {
    const emailRef = useRef<HTMLInputElement>(null);
    const confirmEmailRef = useRef<HTMLInputElement>(null);

    const validationConfig: Config = {
        fields: {
            FirstName: [{ type: 'required', helperText: 'Please enter your first name' }],
            LastName: [{ type: 'required', helperText: 'Please enter your last name' }],
            Email: [
                {
                    type: 'email',
                    helperText: 'Please enter your valid email',
                    validateAlso: [confirmEmailRef.current],
                },
                {
                    type: 'required',
                },
            ],
            confirmEmail: [
                { type: 'matchesCaseInsensitive', config: emailRef.current, helperText: "Email address doesn't match" },
                { type: 'email' },
            ],
            JobTitle: [{ type: 'required', helperText: 'Please enter your job title' }],
            Company: [{ type: 'required', helperText: 'Please enter your company name' }],
            comments: [{ type: 'required', helperText: 'Please enter your business needs' }],
        },
    };

    const validator = useValidator(validationConfig);
    const [submitState, setSubmitState] = useState<SubmitState>('untouched');
    const [validations, setValidations] = useState(validator.getDefault);
    const errorCount: number = useErrorCount(validations);

    useEffect(() => {
        const browserWindow = window as BrowserWindow;

        if (browserWindow._sxmDataLayerPush) {
            if (submitState === 'success') {
                browserWindow._sxmDataLayerPush('contact-us-success');
            } else if (submitState === 'failed') {
                browserWindow._sxmDataLayerPush('contact-us-fail');
            }
        }
    }, [submitState]);

    function handleValidateForm(event: React.SyntheticEvent) {
        return validateForm(event, validations, setValidations, validator);
    }

    function handleValidateInput(event: React.SyntheticEvent) {
        validateInput(event, validations, setValidations, validator);
    }

    async function formSuccess(response: Response) {
        const json = await response.json();

        if (json.formSubmitResponse.status.message === 'ERROR') {
            const serverValidations: Fields = {};

            json.formSubmitResponse.errors.fields.forEach((field: ServerFields) => {
                serverValidations[field.name] = {
                    value: validations[field.name].value,
                    invalid: VALIDITY.invalid,
                    validations: { ...validations[field.name].validations },
                    helperText: { ...validations[field.name].helperText },
                };

                if (field.name === 'form') {
                    if (field.error === 'unknown') {
                        setSubmitState('failed');
                    }
                } else {
                    delete serverValidations.form;
                }
            });

            setValidations({ ...validations, ...serverValidations });
        } else if (json.formSubmitResponse.status.message === 'SUCCESS') {
            setSubmitState('success');
        }
    }

    function formFail() {
        setSubmitState('failed');
    }

    function renderSubmitMessage() {
        if (submitState === 'success') {
            return (
                <>
                    <Headline desktopCols={10} tag="h3" className="h4">
                        Your information was successfuly submitted! We look forward to reviewing it.
                    </Headline>
                </>
            );
        }

        if (submitState === 'failed') {
            return (
                <>
                    <Headline desktopCols={10} tag="h3" className="h4">
                        There is something wrong :(
                    </Headline>
                    <Grid container>
                        <Grid col={{ sm: 12, lg: 10 }} start={{ lg: 2 }}>
                            <Text tag="p" style={{ paddingTop: 30 }}>
                                Please check and try submitting again
                            </Text>
                        </Grid>
                    </Grid>
                </>
            );
        }
    }

    return (
        <>
            <div aria-live="polite">{renderSubmitMessage()}</div>

            {submitState === 'untouched' ? (
                <Form
                    action="/p/formsubmit/SaveDataServlet?tes=23"
                    method="POST"
                    contentType="urlencoded"
                    onSubmit={handleValidateForm}
                    onSuccess={formSuccess}
                    onFail={formFail}
                    onChange={handleValidateForm}
                    novalidate={true}
                    data-form-name="bussiness-contact-us"
                    data-cy="partner-contact-us-form-form"
                >
                    <input type="hidden" name="formId" value="7" />
                    <input type="hidden" name="environment" value={piiEnv} />
                    <input type="hidden" name="evflag" value="" />
                    <input type="hidden" name="guid" value="" />

                    <Grid container>
                        <Grid col={{ sm: 12, lg: 10 }} start={{ lg: 2 }}>
                            <FormErrorDisplay errorCount={errorCount} />

                            <Fieldset theme="themeGrey" legend="">
                                <Grid container>
                                    <Grid col={{ sm: 12, lg: 6 }}>
                                        <Input
                                            type="text"
                                            name="FirstName"
                                            label="First name"
                                            invalid={validations.FirstName.invalid}
                                            required={true}
                                            onBlur={handleValidateInput}
                                            invalidMessage={validations.FirstName.helperText.required}
                                        />

                                        <Input
                                            type="text"
                                            name="LastName"
                                            label="Last name"
                                            invalid={validations.LastName.invalid}
                                            required={true}
                                            onBlur={handleValidateInput}
                                            invalidMessage={validations.LastName.helperText.required}
                                        />

                                        <Input
                                            type="text"
                                            name="Email"
                                            label="Email"
                                            ref={emailRef}
                                            invalid={validations.Email.invalid}
                                            required={true}
                                            onBlur={handleValidateInput}
                                            invalidMessage={validations.Email.helperText.email}
                                        />

                                        <Input
                                            type="text"
                                            name="confirmEmail"
                                            label="Confirm email"
                                            ref={confirmEmailRef}
                                            invalid={validations.confirmEmail.invalid}
                                            required={true}
                                            onBlur={handleValidateInput}
                                            invalidMessage={validations.confirmEmail.helperText.matchesCaseInsensitive}
                                        />
                                    </Grid>

                                    <Grid col={{ sm: 12, lg: 6 }}>
                                        <Input
                                            type="text"
                                            name="JobTitle"
                                            label="Job title"
                                            invalid={validations.JobTitle.invalid}
                                            onBlur={handleValidateInput}
                                            required={true}
                                            invalidMessage={validations.JobTitle.helperText.required}
                                        />
                                        <Input
                                            type="text"
                                            name="Company"
                                            label="Company name"
                                            invalid={validations.Company.invalid}
                                            onBlur={handleValidateInput}
                                            required={true}
                                            invalidMessage={validations.Company.helperText.required}
                                        />

                                        <Textarea
                                            name="comments"
                                            label="Briefly describe your business needs"
                                            required={true}
                                            invalid={validations.comments.invalid}
                                            onBlur={handleValidateInput}
                                            invalidMessage={validations.comments.helperText.required}
                                        />
                                    </Grid>
                                </Grid>
                            </Fieldset>
                        </Grid>

                        <Grid col={{ sm: 12, lg: 6 }} start={{ lg: 4 }}>
                            {validations.form && validations.form.validations.formerror && (
                                <StyledContainer paddingTop={40}>
                                    <Text tag="div" style={{ color: 'var(--red)', fontWeight: 'bold' }} role="alert">
                                        {validations.form.helperText.formerror}
                                    </Text>
                                </StyledContainer>
                            )}

                            <Button
                                type="submit"
                                ariaLabel="submit contact us"
                                variant="primary"
                                fitting="fill-container"
                            >
                                Submit
                            </Button>
                        </Grid>
                    </Grid>
                </Form>
            ) : undefined}
        </>
    );
}
